import { Injectable } from "@angular/core";

import { BaseHandlerErrorMapper } from "@app/core/shared/data/network/base-handler-error.mapper";
import { Observable, catchError, finalize, throwError, timeout } from "rxjs";
import * as Sentry from "@sentry/angular";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { TranslateService } from "@ngx-translate/core";
import {
  HandlerParameters,
  HttpMethod,
} from "@app/core/shared/data/network/base-handler.parameters";
import { RequestCache } from "@app/core/shared/data/network/request-cache";
import { VERSION } from "src/autogenerated-version";
import { environment } from "src/environments/environment";
import { Router } from "@angular/router";
import { RoutesSlugs } from "@app/routes-slugs";
import { InfrastructureError, InfrastructureErrorType } from "@app/core/shared/data/network/errors/infrastructure.error";
import { ForceErrorsDAO } from "@app/core/shared/data/local/force-errors.dao";

@Injectable()
export class BaseHandler {
  // Chance a request is failed instead of sent while the admin-only "Force errors" debug
  // flag is on (see AdminImpersonatePage), so QA can see how the UI reacts to failures.
  private readonly FORCE_ERROR_PROBABILITY = 0.3;

  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
    private router: Router,
    private forceErrorsDAO: ForceErrorsDAO,
    private requestCache: RequestCache
  ) {
    this.errorHandler = BaseHandlerErrorMapper.handleError;
  }

  errorHandler = BaseHandlerErrorMapper.handleError;

  execute(parameters: HandlerParameters): Observable<any> {
    // Add the language code to the header.
    const browserLanguage = this.translateService.getBrowserLang();
    parameters.header["Language-Code"] =
      this.translateService.currentLang || browserLanguage || "";
    parameters.header["App-Version"] = VERSION;

    if (parameters.method !== HttpMethod.get) {
      // Any write may change what cached GETs return. Clear before and after,
      // so a GET made while the write is in flight isn't kept either.
      this.requestCache.clear();
      return this.send(parameters).pipe(
        finalize(() => this.requestCache.clear())
      );
    }
    if (parameters.cacheTtlMs != null) {
      return this.requestCache.get(
        this.cacheKey(parameters),
        parameters.cacheTtlMs,
        () => this.send(parameters)
      );
    }
    return this.send(parameters);
  }

  private send(parameters: HandlerParameters): Observable<any> {
    if (
      this.forceErrorsDAO.isEnabled() &&
      Math.random() < this.FORCE_ERROR_PROBABILITY
    ) {
      return throwError(
        () => new HttpErrorResponse({ status: 500, url: parameters.url })
      ).pipe(
        catchError((errorResponse) =>
          this.handleHttpError(errorResponse, parameters)
        )
      );
    }

    const requestOptions = {
      body: parameters.body,
      headers: parameters.header,
      params: parameters.urlParams,
      responseType: parameters.responseType,
    };
    const httpRequest = this.http
      .request(parameters.method, parameters.url, requestOptions);

    return environment.NAME === "PROD"
      ? httpRequest.pipe(
          timeout(10000),
          catchError((errorResponse) =>
            this.handleHttpError(errorResponse, parameters)
          )
        )
      : httpRequest.pipe(
          catchError((errorResponse) =>
            this.handleHttpError(errorResponse, parameters)
          )
        );
  }

  // Includes the token and language, since the response depends on both.
  private cacheKey(parameters: HandlerParameters): string {
    const sortedParams = Object.keys(parameters.urlParams)
      .sort()
      .map((key) => [key, parameters.urlParams[key]]);
    return JSON.stringify([
      parameters.url,
      sortedParams,
      parameters.header["Authorization"],
      parameters.header["Language-Code"],
    ]);
  }

  private handleHttpError(
    errorResponse: unknown,
    parameters: HandlerParameters
  ): Observable<never> | Observable<false> {
    if ((errorResponse as { name?: string })?.name === "TimeoutError") {
      this.captureTimeoutError(parameters);
      throw new InfrastructureError(
        InfrastructureErrorType.TimeOutError,
        errorResponse,
        "error_timeout"
      );
    }

    const httpErrorResponse = errorResponse as HttpErrorResponse;

    if (httpErrorResponse.status === 0) {
      this.navigateToNoInternet();
    }

    return this.errorHandler(httpErrorResponse);
  }


  private captureTimeoutError(parameters: HandlerParameters): void {
    Sentry.captureMessage("Backend request timeout", {
      level: "error",
      tags: {
        category: "network",
      },
      extra: {
        requestMethod: parameters.method,
        requestUrl: parameters.url,
      },
    });
  }

  private navigateToNoInternet(): void {
    const targetUrl = `/${RoutesSlugs.NO_INTERNET}`;
    if (this.router.url.startsWith(targetUrl)) {
      return;
    }

    this.router.navigate([targetUrl], {
      queryParams: { returnUrl: this.router.url },
    });
  }
}
